import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';

import List from 'antd/es/list';
import Divider from 'antd/es/divider';
import Input from 'antd/es/input';
import Spin from 'antd/es/spin';
import Tooltip from 'antd/es/tooltip';

import PayForPerformanceTiersSettingsService from 'services/p4pTiersSettings';

import { Title, Text } from 'components/Text';
import { Row, Col } from 'components/layout';
import Button from 'components/Button';

const p4pTiersSettingsService = new PayForPerformanceTiersSettingsService();

const validateTier = (tier, required) => {
  let errors = {};
    
  if (tier.from_mins !== null && tier.from_mins !== null) {
    if (tier.from_mins >= tier.to_mins) {
      errors.mins = `Saved mins range is invalid`;
    }
  } else if (required) {
    errors.mins = 'Saved mins range is required';
  }

  if (tier.base_increase !== null) {
    if (tier.base_increase <= 0 ) {
      errors.base_increase = 'Rate increase should be greater or equal to 0'
    }
  } else if (required) {
    errors.base_increase = 'Rate increase is required';
  }

  return errors;
};

const PayForPerformanceTiersTab = ({ showMessage }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tiersList, setTiersList] = useState([]);

  const hasChanges = useMemo(() => {
    return tiersList.some(item => item.modified || item.deleted);
  }, [tiersList]);

  const hasErrors = useMemo(() => {
    return tiersList.some(item => !item.deleted &&
        item.errors && Object.keys(item.errors).length > 0);
  }, [tiersList]);

  const fetchP4PTiers = useCallback(async () => {
    const data = await p4pTiersSettingsService.getP4PTiers();
    if (!data) {
      setTiersList([]);
    } else {
      setTiersList(data.map(item => ({
        ...item,
        modified: false,
        deleted: false,
        errors: {},
      })));
    }
  }, []);

  useEffect(() => {
    setIsLoading(true);
    fetchP4PTiers().finally(() => {
      setIsLoading(false);
    });
  }, [fetchP4PTiers]);

  const onSaveChanges = useCallback(() => {
    if (hasErrors) {
      return;
    }

    setIsSubmitting(true);
    
    const notDeletedTiers = tiersList.filter(tier => !tier.deleted);
    p4pTiersSettingsService.updateP4PTiers({
      Values: notDeletedTiers.map(tierItem =>
        [
          'id',
          'name',
          'base_increase',
          'from_mins',
          'to_mins',
        ].reduce((item, field) => {
          item[field] = tierItem[field];
          return item;
        }, {})
      ),
    }).then(() => {
      showMessage('success', 'Pay for Performance Tiers settings updated');
      fetchP4PTiers();
    })
    .catch(() => {
      showMessage('error', 'Failed to save Pay for Performance Tiers settings');
    })
    .finally(() => {
      setIsSubmitting(false);
    });
  }, [tiersList, hasErrors, showMessage, fetchP4PTiers]);

  // TODO: probably refactor into a useReducer
  const onItemAdd = useCallback(() => {
    const lastNotDeletedItem = [...tiersList].reverse().find(item => !item.deleted);
    const nextNumber = lastNotDeletedItem ? parseInt(lastNotDeletedItem.name.replace('Tier', ''), 10) + 1 : 1;
    setTiersList([
      ...tiersList,
      {
        name: `Tier ${nextNumber}`,
        from_mins: null,
        to_mins: null,
        base_increase: null,
        modified: false,
        deleted: false,
        errors: {},
      }
    ]);
  }, [tiersList]);

  const onItemChanged = useCallback((tierIndex, tier) => {
    setTiersList(tiersList =>
      tiersList.map((item, i) => {
        if (i === tierIndex - 1) {
          item = { ...item, to_mins: tier.from_mins, modified: true };
        }
        if (i === tierIndex) {
          item = { ...tier, modified: true };
        }
        if (i=== tierIndex + 1) {
          item = { ...item, from_mins: tier.to_mins, modified: true };
        }
        return item;
      })
    );
  }, []);

  const onItemRemoved = useCallback((tierIndex) => {
    setTiersList(tiersList => tiersList.map((item, i) => {
      if (i === tierIndex) {
        item = { ...item, deleted: true };
      }
      return item;
    }));
  }, []);

  const onItemError = useCallback((tierIndex, errors) => {
    setTiersList(tiersList => tiersList.map((item, i) => {
      if (i === tierIndex) {
        item = { ...item, errors };
      }
      return item;
    }));
  }, []);

  if (isLoading) {
    return (
      <Spin
        size={30}
        style={{ width: '100%', padding: '36px' }}
      />
    );
  }

  return (
    <Row justify="center">
      <Col span={18}>
        <Title
          level={4}
          style={{
            textAlign: 'center',
            marginBottom: '32px'
          }}
        >
          Pay for Performance Tiers
        </Title>
        <StyledTierList
          header={(
            <Row>
              <Col offset={8} span={16}>
                <Row gutter={24}>
                  <Col span={12}>
                    <Text style={{ fontWeight: 'bold' }}>Saved Mins Range</Text>
                  </Col>
                  <Col span={12}>
                    <Text style={{ fontWeight: 'bold' }}>Rate Increase</Text>
                  </Col>
                </Row>
              </Col>
            </Row>
          )}
          itemLayout="horizontal"
          dataSource={tiersList}
          locale={{
            emptyText: "No tiers"
          }}
          renderItem={(tier, tierIndex) => {
            if (tier.deleted) {
              return null;
            }

            return (
              <List.Item key={tierIndex}>
                <Row justify="space-between" style={{ minHeight: '36px', width: '100%' }}>
                  <Col span={6}>
                    <List.Item.Meta
                      title={tier.name}
                      // description={'Set increase for each tier range'}
                    />
                  </Col>
                  <Col offset={2} span={16}>
                    <TierItem
                      tierIndex={tierIndex}
                      tier={tier}
                      isSaving={isSubmitting}
                      onChange={onItemChanged}
                      onRemove={onItemRemoved}
                      onError={onItemError}
                    />
                  </Col>
                </Row>
              </List.Item>
            );
          }}
        />
        <>
          <Divider type="horizontal" style={{ marginTop: 0, marginBottom: '16px' }} />
          <Row justify="end" style={{ padding: '16px 0' }}>
            <Button
              type="default"
              icon={{ name: "plus", size: 14 }}
              style={{ marginBottom: '4px', marginLeft: '8px' }}
              onClick={onItemAdd}
            >
              Add New Tier
            </Button>
          </Row>
        </>
        <Row justify="center" style={{ padding: '16px 0' }}>
          <Button
            type="primary"
            loading={isSubmitting}
            disabled={!hasChanges || hasErrors}
            size="large"
            onClick={onSaveChanges}
            style={{
              width: '350px'
            }}
          >
            Save Changes
          </Button>
        </Row>

      </Col>
    </Row>
  );
};

const TierItem = ({ tier, tierIndex, isSaving, onChange, onRemove, onError }) => {
  const { errors } = tier;

  useEffect(() => {
    const errors = validateTier(tier, tier.modified);
    const hasChanges = JSON.stringify(errors) !== JSON.stringify(tier.errors);
    if (hasChanges && typeof onError === 'function') {
      const t = setTimeout(() => {
        onError(tierIndex, errors);
      }, 200);

      return () => {
        clearTimeout(t);
      };
    }
  }, [tier, tierIndex, isSaving, onError]);

  return (
    <Row gutter={24}>
      <Col span={12}>
        <InputWrapperWithFeeback
          message={errors.mins || ''}
          status="error"
        >
          <RangeInputWrapper>
            <Input
              type="number"
              value={tier.from_mins}
              placeholder="From Mins"
              onChange={(e) => {
                let value = null;
                if (e.target.value !== '') {
                  value = Number(e.target.value);
                  if (isNaN(value)) {
                    return;
                  }
                }
                onChange(tierIndex, { ...tier, from_mins: value });
              }}
              style={{ 
                maxWidth: '140px'
              }}
            />
            <Input
              type="number"
              value={tier.to_mins}
              placeholder="To Mins"
              onChange={(e) => {
                let value = null;
                if (e.target.value !== '') {
                  value = Number(e.target.value);
                  if (isNaN(value)) {
                    return;
                  }
                }
                onChange(tierIndex, { ...tier, to_mins: value });
              }}
              style={{ 
                maxWidth: '140px'
              }}
            />
          </RangeInputWrapper>
        </InputWrapperWithFeeback>
      </Col>
      <Col span={10}>
        <InputWrapperWithFeeback
          message={errors.base_increase || ''}
          status="error"
        >
          <Input
            type="number"
            value={tier.base_increase}
            placeholder={'Rate Increase'}
            addonAfter="USD"
            onChange={(e) => {
              let value = null;
              if (e.target.value !== '') {
                value = Number(e.target.value);
                if (isNaN(value)) {
                  return;
                }
              }
              onChange(tierIndex, { ...tier, base_increase: value });
            }}
            style={{ 
              maxWidth: '220px'
            }}
          />
        </InputWrapperWithFeeback>
      </Col>
      <Col span={1}>
        <Tooltip trigger="hover" title="Remove Tier">
          <Button
            type="text"
            icon={{ name: 'x-circle', size: 16, color: 'rgba(0, 0, 0, 0.65)' }}
            disabled={isSaving}
            onClick={() => {
              onRemove(tierIndex);
            }}
          />
        </Tooltip>
      </Col>
    </Row>
  );
};


const StyledTierList = styled(List)`
  min-width: 75%;

  & .ant-list-item {
    padding: 16px 0;
  }

  & .ant-list-item-meta {
    margin-right: 16px;
  }

  & .ant-list-item-meta-title {
    font-family: Inter;
    font-style: normal;
    font-weight: 600;
    font-size: 16px;
    line-height: 24px;
    margin-bottom: 0;
  }

  & .ant-list-item-meta-description {
    font-family: Inter;
    font-style: normal;
    font-weight: normal;
    font-size: 12px;
    line-height: 20px;
  }
`;

const RangeInputWrapper = styled.div`
  display: flex;
  flex-wrap: nowrap;
  border-radius: 4px;
  border: 1px solid #d9d9d9;

  &:focus-within {
    border-color: #6bc9e3;
    border-right-width: 1px !important;
    outline: 0;
    box-shadow: 0 0 0 2px rgb(66 176 213 / 20%);
  }

  & > input {
    border: none;
  }

  & > input:focus {
    border: none;
    box-shadow: none;
  }

  & > input:nth-child(odd) {
    border-right: none;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  & > input:nth-child(even) {
    border-left: none;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }
`;

const InputWrapperWithFeeback = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  position: relative;

  &:after {
    visibility: ${props => props.message ? 'visible' : 'hidden'};
    position: absolute;
    top: 100%; // height of input
    left: 4px;
    content: '${props => props.message}';
    color: rgba(0, 0, 0, .75);
    font-size: 11px;
    line-height: 1.5;
    transition: visibility .3s cubic-bezier;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: wrap;
    max-width: 100%;

    ${props => props.message && props.status === 'error' ? `
      color: #ff4d4f;
    ` : null}
  }
`;

export default PayForPerformanceTiersTab;